import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, FlaskConical, FileCheck, Truck, MapPin,
  Zap, Sun, Flame, BatteryCharging, ShieldCheck, Moon, Brain, Sparkles, Droplet,
  BookOpen, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 9 pathway tiles — colors per design_guidelines + your earlier user spec.
const PATHWAYS = [
  { tag: 'Growth Hormone Secretagogues', short: 'Growth Hormone Secretagogues', icon: Zap, theme: 'red',     blurb: 'Pituitary GH-axis & ghrelin pathway research peptides.' },
  { tag: 'Melanocortin Receptor Agonists', short: 'Melanocortin Receptor Agonists', icon: Sun, theme: 'amber', blurb: 'α-MSH analogs & melanocortin receptor signaling.' },
  { tag: 'Metabolic Pathway Modulators', short: 'Metabolic Pathway Modulators', icon: Flame, theme: 'orange', blurb: 'GLP-1, amylin, hGH-fragment metabolic targets.' },
  { tag: 'Mitochondrial Function Modulators', short: 'Mitochondrial Function Modulators', icon: BatteryCharging, theme: 'yellow', blurb: 'AMPK, sirtuin, redox-pathway research.' },
  { tag: 'Inflammatory Pathway Modulators', short: 'Inflammatory Pathway Modulators', icon: ShieldCheck, theme: 'green', blurb: 'BPC-157, TB-500, KPV — inflammation signaling.' },
  { tag: 'Endocrine & Circadian Neuropeptides', short: 'Endocrine & Circadian Neuropeptides', icon: Moon, theme: 'indigo', blurb: 'DSIP and circadian-axis research peptides.' },
  { tag: 'Neuropeptides', short: 'Neuropeptides', icon: Brain, theme: 'cyan', blurb: 'Semax, Selank, Adamax — neurotrophic signaling.' },
  { tag: 'Copper-Binding Tripeptides', short: 'Copper-Binding Tripeptides', icon: Sparkles, theme: 'blue', blurb: 'GHK-Cu, AHK-Cu fibroblast & follicular research.' },
  { tag: 'Diluents', short: 'Diluents', icon: Droplet, theme: 'pink', blurb: 'Sterile bacteriostatic water for reconstitution.' },
];

const THEME = {
  red:    { bg: 'bg-red-50',    text: 'text-red-900',    border: 'border-red-200',    accent: 'text-red-600' },
  amber:  { bg: 'bg-amber-50',  text: 'text-amber-900',  border: 'border-amber-200',  accent: 'text-amber-700' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-900', border: 'border-orange-200', accent: 'text-orange-600' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-900', border: 'border-yellow-200', accent: 'text-yellow-700' },
  green:  { bg: 'bg-green-50',  text: 'text-green-900',  border: 'border-green-200',  accent: 'text-green-700' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-900', border: 'border-indigo-200', accent: 'text-indigo-600' },
  cyan:   { bg: 'bg-cyan-50',   text: 'text-cyan-900',   border: 'border-cyan-200',   accent: 'text-cyan-700' },
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-900',   border: 'border-blue-200',   accent: 'text-blue-700' },
  pink:   { bg: 'bg-pink-50',   text: 'text-pink-900',   border: 'border-pink-200',   accent: 'text-pink-700' },
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Hero image carousel — always starts with GLP3-RT, then cycles 4 other flagship
// peptides on a slow fade-in/fade-out. Pinned image URLs (intentionally not
// pulled from the live product list so the rotation is curated, not random).
const HERO_IMAGES = [
  { name: 'GLP3-RT',      src: 'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/lhp87bxd_GLP3-RT%20-%2030MG.png' },
  { name: 'BPC-157',      src: 'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/7vstog2u_BPC-157%20-%2010MG.png' },
  { name: 'Tesamorelin',  src: 'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/q42amnsh_Tesamorelin%20-%2010MG.png' },
  { name: 'NAD+',         src: 'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/x8c8f1sf_NAD%2B%20-%201000MG.png' },
  { name: 'Cagrilintide', src: 'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/2ulnjm2d_Cagrilintide%20-%2010MG.png' },
];
const HERO_INTERVAL_MS = 4500;

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const featuredRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/products?featured=true&limit=8`).then((r) => setFeatured(r.data.products || [])).catch(() => {});
  }, []);

  // Hero rotation — pauses when the tab is hidden so we don't waste cycles.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.hidden) return undefined;
    const t = setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, HERO_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const scrollFeatured = (dir) => {
    if (!featuredRef.current) return;
    featuredRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-slate-900" data-testid="test-landing-page">
      <SEO
        title="Research-Grade Peptides — A Cleaner Way to Source"
        description="Wilvada redesigned: clinical-grade peptides with HPLC ≥99% purity, COA on every order, free standard shipping at $100+. Strictly for in-vitro and pre-clinical research."
      />

      {/* ─── HERO ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-slate-50" data-testid="landing-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'><circle cx='120' cy='120' r='80' fill='none' stroke='%231b2e47' stroke-width='1'/><circle cx='120' cy='120' r='40' fill='none' stroke='%231b2e47' stroke-width='1'/><line x1='40' y1='120' x2='200' y2='120' stroke='%231b2e47' stroke-width='1'/><line x1='120' y1='40' x2='120' y2='200' stroke='%231b2e47' stroke-width='1'/></svg>\")",
            backgroundSize: '240px 240px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 max-w-2xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] mb-4 font-body" data-testid="landing-eyebrow">
              · Pre-Order Wave 01 · Open Through June 30 · COAs at Fulfillment ·
            </p>
            <h1 className="font-heading text-5xl lg:text-7xl tracking-tight leading-[1.05] text-slate-900 font-light">
              Reserve your batch from the next wave of <span className="italic text-[hsl(210,45%,40%)]">research-grade</span> peptides.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed font-body max-w-xl">
              Twenty research peptides. Nine signaling pathways. Pre-orders are open now — every vial in Wave 01 ships
              with a Certificate of Analysis, every order over $100 ships free, and your card secures your spot at launch pricing.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 px-7 bg-[hsl(210,52%,21%)] hover:bg-slate-800 text-white rounded-md text-sm font-medium" data-testid="hero-shop-cta">
                <Link to="/catalog">Reserve Your Pre-Order <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-7 rounded-md text-sm font-medium border-slate-300" data-testid="hero-preorder-policy-cta">
                <Link to="/pre-orders">How pre-orders work</Link>
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-slate-500 font-body">
              <span className="inline-flex items-center gap-1.5"><FileCheck className="h-3.5 w-3.5 text-emerald-600" /> COA on every vial</span>
              <span className="hidden sm:inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-emerald-600" /> Free shipping at $100</span>
              <span className="hidden md:inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-emerald-600" /> US ships only</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative" aria-hidden="true">
            <div className="aspect-[4/5] rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden relative" data-testid="hero-image-carousel">
              {HERO_IMAGES.map((peptide, i) => (
                <img
                  key={peptide.name}
                  src={peptide.src}
                  alt={peptide.name}
                  className="absolute inset-0 w-full h-full object-cover object-center mix-blend-multiply transition-opacity duration-1000 ease-in-out"
                  style={{ opacity: i === heroIndex ? 1 : 0 }}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  data-testid={`hero-image-${slugify(peptide.name)}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-50/50 via-transparent to-white/40" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-amber-400/95 text-slate-900 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] uppercase shadow-sm" data-testid="hero-preorder-badge">
                Pre-Order Open
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 max-w-[220px]">
              <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-emerald-700 font-body">Wave 01 closes</p>
              <p className="text-sm text-slate-900 mt-1 leading-snug">June 30, 2026 · COAs issued before each shipment</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST STRIP ──────────────────────────────── */}
      <section className="border-y border-slate-200 bg-white" data-testid="landing-trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200">
          {[
            { icon: FlaskConical, label: 'HPLC ≥99% Purity', sub: 'Every batch, every vial' },
            { icon: FileCheck,   label: 'COA Included',     sub: 'Downloadable in your order' },
            { icon: Truck,       label: 'Free Standard $100+', sub: 'Free 2-Day at $175+' },
            { icon: MapPin,      label: 'Ships from the US', sub: 'Continental US only' },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-3 py-6 px-4 sm:px-6">
              <t.icon className="h-5 w-5 text-[hsl(210,45%,40%)] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 font-body leading-tight">{t.label}</p>
                <p className="text-[11px] text-slate-500 font-body">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── SHOP BY PATHWAY (Bento) ──────────────────── */}
      <section className="py-20 lg:py-28" data-testid="landing-pathways">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] mb-3 font-body">Shop by Pathway</p>
              <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-light text-slate-900 leading-tight">
                Curated by the biology, not the brand.
              </h2>
              <p className="mt-3 text-slate-600 font-body">Every product is filed under the receptor or pathway it interacts with. Pick the science first.</p>
            </div>
            <Link to="/catalog?product_type=peptide" className="hidden sm:inline-flex items-center gap-1 text-sm text-[hsl(210,45%,40%)] hover:underline font-body">
              All peptides <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {PATHWAYS.map((p, i) => {
              const t = THEME[p.theme];
              return (
                <motion.div key={p.tag} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}>
                  <Link
                    to={`/catalog?tag=${encodeURIComponent(p.tag)}`}
                    className={`group block h-full rounded-2xl border ${t.border} ${t.bg} p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300`}
                    data-testid={`pathway-tile-${slugify(p.short)}`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <p.icon className={`h-7 w-7 ${t.accent}`} />
                      <ArrowUpRight className={`h-4 w-4 ${t.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </div>
                    <h3 className={`font-heading text-lg font-medium ${t.text} leading-snug`}>{p.short}</h3>
                    <p className={`mt-2 text-xs ${t.text} opacity-80 font-body leading-relaxed`}>{p.blurb}</p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURED PRODUCTS (scroll-snap) ──────────── */}
      {featured.length > 0 && (
        <section className="py-20 lg:py-28 bg-slate-50 border-t border-slate-200" data-testid="landing-featured">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] mb-3 font-body">Featured</p>
                <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-light text-slate-900">In stock & ready to ship.</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => scrollFeatured(-1)} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors" aria-label="Scroll left" data-testid="featured-scroll-left"><ChevronLeft className="h-4 w-4" /></button>
                <button onClick={() => scrollFeatured(1)} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors" aria-label="Scroll right" data-testid="featured-scroll-right"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
            <div ref={featuredRef} className="flex gap-5 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory -mx-4 px-4">
              {featured.map((p) => (
                <div key={p.id} className="flex-shrink-0 w-[280px] sm:w-[300px] snap-start" data-testid={`featured-card-${p.id}`}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── HOW IT WORKS ─────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] mb-3 font-body">How it works</p>
            <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-light text-slate-900 leading-tight">Three steps, zero ambiguity.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n: '01', title: 'Browse the catalog', body: 'Filter by pathway. Every listing shows molecular profile, sequence class, storage, and a downloadable CoA.' },
              { n: '02', title: 'Verify & checkout', body: 'Confirm Age 21+ and Research-Use-Only at checkout. Shipping addresses are USPS-verified for accuracy.' },
              { n: '03', title: 'Receive with COA', body: 'Lyophilized peptide ships from a US fulfillment partner. Tracking is live in your dashboard the moment it leaves.' },
            ].map((s) => (
              <div key={s.n} className="relative">
                <p className="font-heading text-7xl text-slate-200 absolute -top-6 -left-2 z-0 select-none leading-none">{s.n}</p>
                <div className="relative z-10">
                  <h3 className="font-heading text-xl font-medium text-slate-900 mt-2">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 font-body leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── EDUCATIONAL HUB TEASER (dark) ────────────── */}
      <section className="bg-slate-900 text-slate-50" data-testid="landing-edu-hub">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[hsl(210,40%,75%)] mb-4 font-body">
              <BookOpen className="h-3.5 w-3.5" /> Educational Hub
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl tracking-tight font-light leading-tight">
              Every peptide, three reputable sources, zero marketing copy.
            </h2>
            <p className="mt-5 text-slate-300 font-body max-w-2xl leading-relaxed">
              The Reference Library curates peer-reviewed studies and pharmacology data from the U.S. National Library of Medicine, NCBI PubChem, and DrugBank.
              For research and education only — never medical advice.
            </p>
            <Button asChild size="lg" className="mt-8 h-12 px-7 bg-white text-slate-900 hover:bg-slate-100 rounded-md font-medium" data-testid="edu-hub-cta">
              <Link to="/references">Open the Reference Library <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="lg:col-span-5 grid gap-3">
            {[
              { src: 'PubMed (NIH NLM)',     desc: 'Peer-reviewed peptide literature' },
              { src: 'PubChem (NIH NCBI)',   desc: 'Compound structure & bioactivity' },
              { src: 'DrugBank',             desc: 'Targets, pharmacology, mechanism' },
            ].map((c) => (
              <div key={c.src} className="border border-slate-700 rounded-xl px-5 py-4 hover:border-slate-500 hover:bg-slate-800/50 transition-colors flex items-center justify-between gap-4" data-testid={`edu-source-${slugify(c.src)}`}>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[hsl(210,40%,75%)] font-body font-semibold">{c.src}</p>
                  <p className="text-sm text-slate-100 mt-0.5">{c.desc}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLOSING COMPLIANCE BAND ──────────────────── */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="landing-compliance">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 font-body">Compliance</p>
          <p className="mt-3 text-sm text-slate-700 font-body leading-relaxed">
            All Wilvada LC products are intended <strong>strictly for in-vitro and pre-clinical research</strong>. Not for human or veterinary consumption, diagnostic use, or therapeutic application.
            By purchasing, you confirm you are at least 21 years old and represent a qualified research entity.
          </p>
        </div>
      </section>
    </div>
  );
}
